import { lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarClock, Megaphone, ReceiptText } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ChartAreaSkeleton, ChartCard, ChartLegend } from '@/components/ui/ChartCard';
import { KpiCard } from '@/components/ui/KpiCard';
import { RankingBars } from '@/components/ui/RankingBars';
import { Select } from '@/components/ui/Select';
import { ReportFilters, useReportFiltros } from '@/features/reports/ReportFilters';
import { EmbudoBars } from '@/features/reports/charts/EmbudoBars';
import { HeatmapGrid } from '@/features/reports/charts/HeatmapGrid';
import { kpisResumen } from '@/features/reports/reports-blocks';
import {
  CAMPOS_HORARIO,
  INTERVALOS_SERIE,
  KPIS_EN_ESPERA,
  METRICAS_SERIE,
  PARAM_HORARIO,
  PARAM_METRICA,
  agruparFrom,
  campoHorarioFrom,
  datosPublicidad,
  descripcionSerie,
  describirPeriodoEfectivo,
  describirPico,
  embudoResumen,
  filasRankingCampanas,
  formatCount,
  hayFiltros,
  intervaloFrom,
  metricaFrom,
  metricaSerie,
  rangoRapido,
  resumenSerie,
} from '@/features/reports/reports-format';
import {
  useReportCampanas,
  useReportHorarios,
  useReportResumen,
  useReportSerie,
  useZonaHorariaDeRespuesta,
} from '@/features/reports/use-reports';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { ROUTES } from '@/routes/paths';
import { REPORT_PARAMS } from '@/api/reports';

/**
 * Panel (`/dashboard`, paso 8 del frontend): el resumen ejecutivo del negocio activo.
 *
 * Qué es y qué NO es:
 *  - ES la primera pantalla después del login y responde tres preguntas de un vistazo: ¿cuánto
 *    entró?, ¿de dónde vinieron las conversaciones? y ¿a qué hora trabaja la gente?;
 *  - NO es una tabla. Todo lo que se lee fila por fila (las seis tablas, con `truncado`, límites y
 *    avisos del backend) vive en `/reports`: acá solo se pinta lo que se entiende sin leer;
 *  - NO recalcula nada del backend. Los KPIs son los del resumen, las barras salen de
 *    `filasRankingCampanas` y los porcentajes del embudo son los que manda el backend. Un `null`
 *    («no se pudo calcular») se pinta «—» y se explica en el pie, jamás como un 0.
 *
 * Los cuatro bloques y sus endpoints:
 *  1. KPIs del periodo ............ `GET .../reports/resumen`
 *  2. Serie + embudo .............. `GET .../reports/serie` (+ el resumen del bloque 1)
 *  3. Publicidad y CPA ............ `GET .../reports/campanas` (el mismo gasto que carga Campañas)
 *  4. Horarios de atención ........ `GET .../reports/horarios`
 *
 * La librería de gráficas viaja con `React.lazy`: el panel es la pantalla de entrada y no todo el
 * mundo abre un reporte, así que el gráfico llega en un chunk aparte mientras el resto ya está
 * pintado (el `Suspense` usa el mismo esqueleto que las cargas).
 */
const SeriesChart = lazy(() =>
  import('@/features/reports/charts/SeriesChart').then((modulo) => ({
    default: modulo.SeriesChart,
  })),
);

/*
 * Las opciones de los selectores (intervalo, métrica, conteo del mapa), las etiquetas de los
 * esqueletos y las cinco cifras del resumen NO viven acá: se comparten con Reportes y están en
 * `features/reports/reports-format.ts` y `features/reports/reports-blocks.tsx`, para que las dos
 * pantallas no tengan dos versiones del mismo dato.
 */

/* `kpisResumen` (las cinco cifras del resumen) está en `reports-blocks.tsx`: Reportes pinta las
 * mismas tarjetas con el mismo texto, así que la definición es una sola. */

/* La frase del encabezado del gráfico (`descripcionSerie`) también se comparte: ver
 * `features/reports/reports-format.ts`. */

export function DashboardPage() {
  const { tenantId } = useActiveTenantAccess();
  const [searchParams] = useSearchParams();
  const { filtros, rango, setFiltros, setParam, resetFiltros, zonaHoraria } = useReportFiltros();

  const intervalo = intervaloFrom(searchParams);
  const metrica = metricaFrom(searchParams);
  const agrupar = agruparFrom(searchParams);
  const campoHorario = campoHorarioFrom(searchParams);

  const resumenQuery = useReportResumen({ tenantId, filtros, zonaHoraria });
  const serieQuery = useReportSerie({ tenantId, filtros, zonaHoraria, intervalo });
  const campanasQuery = useReportCampanas({ tenantId, filtros, zonaHoraria, agrupar });
  const horariosQuery = useReportHorarios({ tenantId, filtros, zonaHoraria });

  const resumen = resumenQuery.data;
  const serie = serieQuery.data;
  const campanas = campanasQuery.data;
  const horarios = horariosQuery.data;

  // La zona del negocio se aprende de la primera respuesta (ver `useZonaHorariaDeRespuesta`).
  useZonaHorariaDeRespuesta(
    serie?.zonaHoraria ?? horarios?.zonaHoraria ?? resumen?.periodo.zonaHoraria,
  );

  const metricaDef = metricaSerie(metrica);
  const resumenDeLaSerie = serie
    ? resumenSerie(serie.puntos, metrica, intervalo, zonaHoraria)
    : null;
  const publicidad = campanas ? datosPublicidad(campanas.totales) : null;
  const ranking = campanas ? filasRankingCampanas(campanas.items, 5) : [];
  const periodo =
    resumen?.periodo ?? serie?.periodo ?? horarios?.periodo ?? campanas?.periodo ?? null;
  const periodoVacio = Boolean(
    resumen &&
      resumen.conversaciones.total === 0 &&
      resumen.mensajes.total === 0 &&
      resumen.ordenes.total === 0,
  );
  const enlaceReportes = `${ROUTES.reports}${searchParams.toString() ? `?${searchParams}` : ''}`;

  return (
    <div className="space-y-6">
      <ReportFilters
        filtros={filtros}
        rango={rango}
        zonaHoraria={zonaHoraria}
        onChange={setFiltros}
        onReset={resetFiltros}
        actions={
          <Link
            to={enlaceReportes}
            className="text-caption font-medium text-brand-700 hover:underline"
          >
            Ver el reporte completo
          </Link>
        }
      />

      {periodo ? (
        <p className="-mt-3 text-caption text-ink-faint">
          {hayFiltros(filtros)
            ? `Los cuatro bloques de abajo hablan de ${describirPeriodoEfectivo(periodo, zonaHoraria)}.`
            : `Sin fechas elegidas el backend usa ${describirPeriodoEfectivo(
                periodo,
                zonaHoraria,
              )}: elige un rango arriba para cambiarlo.`}
        </p>
      ) : null}

      {resumenQuery.isError ? (
        <Alert>
          {getErrorMessage(resumenQuery.error)}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 -ml-2"
            onClick={() => void resumenQuery.refetch()}
          >
            Volver a intentar
          </Button>
        </Alert>
      ) : null}

      {periodoVacio ? (
        <Alert tone="info">
          El periodo no tiene actividad: no hubo conversaciones, mensajes ni órdenes. Prueba con un
          rango más amplio o quita los filtros.
        </Alert>
      ) : null}

      <section
        aria-label="Indicadores del periodo"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        {resumenQuery.isError
          ? null
          : resumen
            ? kpisResumen(resumen).map((kpi) => <KpiCard key={kpi.label} {...kpi} />)
            : KPIS_EN_ESPERA.map((etiqueta, indice) => (
                <KpiCard key={indice} label={etiqueta} value="" loading />
              ))}
      </section>

      <div className="grid gap-4 xl:grid-cols-3">
        <ChartCard
          className="xl:col-span-2"
          title="Evolución del periodo"
          description={
            resumenDeLaSerie ? descripcionSerie(resumenDeLaSerie, metricaDef, intervalo) : undefined
          }
          legend={
            <ChartLegend
              items={[
                {
                  label: metricaDef.label,
                  color: metricaDef.color,
                  valor: resumenDeLaSerie
                    ? metricaDef.formatear(resumenDeLaSerie.total)
                    : undefined,
                },
              ]}
            />
          }
          actions={
            <>
              <Select
                aria-label="Métrica del gráfico"
                className="w-40"
                value={metrica}
                onChange={(event) => setParam(PARAM_METRICA, event.target.value)}
              >
                {METRICAS_SERIE.map((opcion) => (
                  <option key={opcion.id} value={opcion.id}>
                    {opcion.corto}
                  </option>
                ))}
              </Select>

              <Select
                aria-label="Intervalo del gráfico"
                className="w-40"
                value={intervalo}
                onChange={(event) => setParam(REPORT_PARAMS.intervalo, event.target.value)}
              >
                {INTERVALOS_SERIE.map((opcion) => (
                  <option key={opcion.value} value={opcion.value}>
                    {opcion.label}
                  </option>
                ))}
              </Select>
            </>
          }
          loading={serieQuery.isPending && !serie}
          error={serieQuery.isError ? getErrorMessage(serieQuery.error) : null}
          onRetry={() => void serieQuery.refetch()}
          empty={
            serie && serie.puntos.length === 0
              ? {
                  title: 'El periodo no tiene puntos que graficar',
                  description:
                    'No hay conversaciones, mensajes, órdenes ni ingresos en estas fechas. Prueba con un rango más amplio.',
                  action: (
                    <Button
                      variant="secondary"
                      onClick={() => setFiltros(rangoRapido('30d', zonaHoraria))}
                    >
                      Ver los últimos 30 días
                    </Button>
                  ),
                }
              : null
          }
          height={300}
        >
          <div className="space-y-3">
            {serie?.truncadoRango ? (
              <Alert tone="warning">
                El rango pedido tiene más puntos de los que el backend grafica de una vez: el gráfico
                muestra los primeros. Acorta el periodo o cambia el intervalo a «Por semana».
              </Alert>
            ) : null}

            <Suspense fallback={<ChartAreaSkeleton height={300} />}>
              <SeriesChart
                puntos={serie?.puntos ?? []}
                metrica={metrica}
                intervalo={intervalo}
                zonaHoraria={zonaHoraria}
                height={300}
              />
            </Suspense>
          </div>
        </ChartCard>

        <Card>
          <CardHeader>
            <CardTitle>Conversaciones → ventas</CardTitle>
            <CardDescription>
              Cuántas conversaciones del periodo llegaron a una orden y cuántas terminaron en venta
              confirmada. Los porcentajes son sobre las conversaciones.
            </CardDescription>
          </CardHeader>

          <CardContent>
            {resumen ? (
              <EmbudoBars pasos={embudoResumen(resumen)} />
            ) : (
              <ChartAreaSkeleton height={200} />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Publicidad y CPA</CardTitle>
            <CardDescription>
              El gasto de las campañas del periodo —el que se puede atribuir a una conversación y el
              que quedó sin atribuir— y qué cuesta cada venta confirmada.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {campanasQuery.isError ? (
              <div className="space-y-3">
                <Alert>{getErrorMessage(campanasQuery.error)}</Alert>
                <Button variant="secondary" size="sm" onClick={() => void campanasQuery.refetch()}>
                  Volver a intentar
                </Button>
              </div>
            ) : publicidad && campanas ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {publicidad.map((dato) => (
                    <div key={dato.id} className="rounded-panel border border-line px-3 py-2">
                      <p className="text-micro text-ink-muted">{dato.label}</p>
                      <p className="tabular text-card-title font-semibold text-ink">{dato.valor}</p>
                      <p className="mt-0.5 text-micro text-ink-faint">{dato.hint}</p>
                    </div>
                  ))}
                </div>

                {campanas.avisos.length > 0 ? (
                  <ul className="space-y-2">
                    {campanas.avisos.map((aviso) => (
                      <li
                        key={aviso}
                        className="flex gap-2 rounded-control bg-surface-muted px-3 py-2 text-micro text-ink-muted"
                      >
                        <ReceiptText size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                        <span>{aviso}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <p className="text-micro text-ink-faint">
                  El gasto se carga en Campañas y el CPA lo calcula el backend; acá solo se muestra.
                  Una campaña con conversaciones y sin gasto queda «sin gasto cargado», que no es lo
                  mismo que un 0.
                </p>
              </>
            ) : (
              <ChartAreaSkeleton height={180} />
            )}
          </CardContent>
        </Card>

        <ChartCard
          className="xl:col-span-2"
          title="Horarios de atención"
          description={
            horarios
              ? `${formatCount(horarios.totales.mensajes)} mensajes del cliente y ${formatCount(
                  horarios.totales.conversacionesIniciadas,
                )} conversaciones nuevas en ${describirPeriodoEfectivo(
                  horarios.periodo,
                  zonaHoraria,
                )}, en las horas del negocio.`
              : undefined
          }
          actions={
            <Select
              aria-label="Qué conteo se intensifica en el mapa"
              className="w-56"
              value={campoHorario}
              onChange={(event) => setParam(PARAM_HORARIO, event.target.value)}
            >
              {CAMPOS_HORARIO.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Select>
          }
          loading={horariosQuery.isPending && !horarios}
          error={horariosQuery.isError ? getErrorMessage(horariosQuery.error) : null}
          onRetry={() => void horariosQuery.refetch()}
          empty={
            horarios &&
            horarios.totales.mensajes === 0 &&
            horarios.totales.conversacionesIniciadas === 0
              ? {
                  title: 'Ninguna hora del periodo tuvo actividad',
                  description:
                    'No llegaron mensajes del cliente ni conversaciones nuevas en estas fechas, así que el mapa quedaría todo gris.',
                }
              : null
          }
        >
          {horarios ? (
            <div className="space-y-3">
              {horarios.truncado ? (
                <Alert tone="warning">
                  El backend alcanzó el tope de filas del periodo: el mapa se dibuja con los datos que
                  alcanzó a contar, así que las horas con actividad pueden estar incompletas.
                </Alert>
              ) : null}

              <HeatmapGrid
                matriz={horarios.matriz}
                campo={campoHorario}
                footer={`La hora más movida del periodo: ${describirPico(
                  horarios.picos[campoHorario],
                  campoHorario,
                )}`}
              />
            </div>
          ) : null}
        </ChartCard>
      </div>

      <Card>
        <CardHeader
          actions={
            <Link
              to={ROUTES.campaigns}
              className="text-caption font-medium text-brand-700 hover:underline"
            >
              Ver el gasto cargado
            </Link>
          }
        >
          <CardTitle>¿Quién trae las conversaciones?</CardTitle>
          <CardDescription>
            <span className="inline-flex items-center gap-1.5">
              <Megaphone size={14} aria-hidden="true" />
              Las cinco filas con más conversaciones del periodo, del mismo reporte que alimenta
              Campañas.
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent>
          {campanasQuery.isError ? (
            <Alert>{getErrorMessage(campanasQuery.error)}</Alert>
          ) : campanas ? (
            <RankingBars
              items={ranking}
              footer="El orden lo decide el backend por conversaciones del periodo. La tabla completa, con el gasto y el CPA de cada fila, está en Reportes."
            />
          ) : (
            <ChartAreaSkeleton height={140} />
          )}
        </CardContent>
      </Card>

      <footer className="flex flex-wrap items-start gap-2 text-micro text-ink-faint">
        <CalendarClock size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          Este panel es un resumen del negocio activo: las seis tablas completas, con sus límites y
          los avisos del backend, están en{' '}
          <Link to={enlaceReportes} className="font-medium text-brand-700 hover:underline">
            Reportes
          </Link>
          .
        </span>
      </footer>
    </div>
  );
}
